<template>
    <div class="searchable-root" :class="{ 'mb-4': margin === true }">
        <div
            v-on-clickaway="reset"
            class="input-wrapper d-flex align-items-center"
            :class="[{ outline: outline }, enumKey]"
        >
            <h6 v-if="value && value.prefix" class="mb-0 pl-2">
                {{ value.prefix }}
            </h6>
            <input
                ref="search"
                v-model="search"
                class="search-input w-100"
                :class="[{ 'pa-2': padding === true }, inputClass]"
                :placeholder="placeholder"
                v-bind="$attrs"
                @input="onChange"
                @focus="showAll"
                @keydown.down="onArrowDown"
                @keydown.up="onArrowUp"
                @keydown.enter="onEnter"
                @keydown.tab="onEnter"
                @blur="handleFocusOut"
            />
            <BaseIcon v-if="enumKey" :name="icon" @click.native="focusSearch" />
            <BaseIcon name="caret-down" @click.native="focusSearch" />
        </div>
        <h6
            v-if="helperText && !isOpen"
            class="subtitle mt-2"
            :class="{ 'error-color': error === true }"
        >
            {{ helperText }}
        </h6>
        <Paper v-show="isOpen" class="results">
            <li
                v-for="(item, i) in results.slice(0, maxResults)"
                :ref="item.value"
                :key="item.value"
                class="result-item"
                :class="{ active: i === arrowCounter }"
                @click="setResult(item)"
            >
                <div class="result-item-content d-flex align-items-center">
                    <h6 v-if="item.prefix" class="caption m-0 mr-2">
                        {{ item.prefix }}
                    </h6>
                    <h6 class="title m-0">{{ item.label }}</h6>
                    <h6 v-if="item.suffix" class="caption m-0 ml-2">
                        {{ item.suffix }}
                    </h6>
                </div>
                <Divider />
            </li>
        </Paper>
    </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import StateEnum, { IconMap } from '~/utils/StateEnum';

export default {
    mixins: [clickaway],
    name: 'Searchable',
    props: {
        data: {
            type: Array,
            required: false,
            default: () => [],
        },
        value: {
            type: Object,
            default: undefined,
        },
        creatable: Boolean,
        creatableLabel: {
            type: String,
            default: undefined,
        }, // Custom label instead of "Create"
        stateEnum: {
            type: Number,
            default: undefined,
        },
        padding: Boolean,
        placeholder: {
            type: String,
            default: '',
        },
        outline: {
            type: Boolean,
            default: true,
        },
        inputClass: {
            type: String,
            default: '',
        },
        error: Boolean,
        helperText: {
            type: String,
            default: '',
        },
        margin: {
            type: Boolean,
            default: false,
        },
        maxResults: {
            type: Number,
            default: 50,
        },
    },
    data: () => ({
        search: '',
        results: [],
        isOpen: false,
        arrowCounter: -1,
    }),
    computed: {
        enumKey() {
            return Object.keys(StateEnum)[this.stateEnum] || '';
        },
        icon() {
            return IconMap[this.stateEnum] || '';
        },
    },
    methods: {
        focusSearch() {
            this.$refs.search.focus();
        },
        focus() {
            this.focusSearch();
        },
        onChange() {
            this.isOpen = true;
            this.filterResults();
        },
        filterResults() {
            const search = this.search.toLowerCase();
            let data = this.data;

            // Filter results based on search
            if (search) {
                const startsWith = [];
                const includes = [];
                this.data.forEach(item => {
                    if (
                        (item.label && item.label.toLowerCase().startsWith(search)) ||
                        (item.suffix && item.suffix.toLowerCase().startsWith(search))
                    ) {
                        startsWith.push(item);
                    } else if (
                        (item.label && item.label.toLowerCase().includes(search)) ||
                        (item.suffix && item.suffix.toLowerCase().includes(search))
                    ) {
                        includes.push(item);
                    }
                });
                data = startsWith.concat(includes);
            }

            // Add extra result if is creatable
            if (this.creatable && search) {
                data.push({
                    label: (this.creatableLabel !== undefined
                        ? this.creatableLabel
                        : this.$t('components.builders.searchable.create')
                    ).concat(" '".concat(this.search, "'")),
                    value: null,
                    created: this.search,
                });
            }
            this.results = data || [];

            // Reset arrow counter if it exceeds the maximum length
            if (this.arrowCounter >= this.results.length) {
                this.arrowCounter = 0;
            }
        },
        showAll() {
            this.isOpen = true;
            this.results = this.data || [];
        },
        setResult(value) {
            if (value.created) {
                value.label = value.created;
                delete value.created;
            }

            this.search = value.label;
            this.$emit('input', value);
            this.reset();
        },
        onArrowDown() {
            if (this.arrowCounter + 1 < this.results.length) {
                this.arrowCounter = this.arrowCounter + 1;

                this.scrollSelectedIntoView();
            }
        },
        onArrowUp() {
            if (this.arrowCounter > 0) {
                this.arrowCounter = this.arrowCounter - 1;

                this.scrollSelectedIntoView();
            }
        },
        onEnter() {
            let selectedValue = null;
            // If no element is selected with the arrows-keys, select the one on the top
            if (this.arrowCounter === -1) {
                selectedValue = this.results.length > 0 ? this.results[0] : null;
            } else {
                selectedValue = this.results[this.arrowCounter];
            }

            // Do not set a null-value
            if (selectedValue !== null) {
                this.setResult(selectedValue);
            }
        },
        reset() {
            this.isOpen = false;
            this.arrowCounter = -1;
        },
        handleFocusOut() {
            this.search = this.value ? this.value.label : '';
        },
        scrollSelectedIntoView() {
            // Scroll into view
            if (this.results.length > this.arrowCounter) {
                const elems = this.$refs[this.results[this.arrowCounter].value] || [];
                const elem = elems.length > 0 ? elems[0] : null; // Important with safe checks
                if (elem && elem.scrollIntoViewIfNeeded) {
                    elem.scrollIntoViewIfNeeded(false);
                }
            }
        },
    },
    mounted() {
        if (this.$props.value) {
            this.search = this.$props.value.label;
        }
    },
    watch: {
        value(newVal) {
            this.search = newVal ? newVal.label : '';
        },
    },
};
</script>

<style scoped>
.searchable-root {
    position: relative;
    width: 100%;
}
.input-wrapper {
    background: white;
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
}
.results {
    position: absolute;
    background-color: white;
    padding: 0;
    margin: 0;
    z-index: 4000;

    border-bottom-left-radius: 3px;
    border-bottom-right-radius: 3px;
    width: 100%;
    max-height: 300px;
    overflow: auto;
}
.search-input {
    padding: 6px;
    height: auto;
    border: none;
    border-radius: 0.25rem;
}
.search-input:focus {
    outline: none;
    background-color: rgba(0, 0, 0, 0.02);
}
.outline {
    border-width: 1px;
    border-style: solid;
    border-radius: 0.25rem;
    border-color: rgba(0, 0, 0, 0.4);
}

/* Result item */
.result-item {
    list-style: none;
    cursor: pointer;
}
.result-item:hover {
    background-color: rgba(0, 0, 0, 0.06);
}
.result-item-content {
    padding: 12px;
}
.active {
    background-color: rgba(0, 0, 0, 0.04);
}
</style>
